import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BarChart, LineChart, MapChart, RadarChart, ScatterChart } from "echarts/charts";
import {
  GeoComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  RadarComponent,
  TooltipComponent,
  VisualMapComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { applyDesignTheme, buildCompositionBar, isRecord, type CompositionBarSegment } from "../chartOptions";
import { ensurePrefectureMap } from "../geo";
import type { DashboardChart, DataTable } from "../types";
import { InfoTooltip } from "./common";
import { SelectionControlGroup } from "./SelectionControls";

type ChartClickParams = {
  name?: unknown;
  data?: unknown;
};

echarts.use([
  BarChart,
  GeoComponent,
  GraphicComponent,
  GridComponent,
  LegendComponent,
  LineChart,
  MapChart,
  RadarChart,
  RadarComponent,
  ScatterChart,
  TooltipComponent,
  VisualMapComponent,
  CanvasRenderer,
]);

interface MapRankEntry {
  name: string;
  value: number;
}

function mapRankLists(option: Record<string, unknown>): { top: MapRankEntry[]; bottom: MapRankEntry[] } {
  const series = Array.isArray(option.series) ? option.series[0] : option.series;
  const data = isRecord(series) && Array.isArray(series.data) ? series.data : [];
  const entries = data
    .filter(isRecord)
    .map((item) => ({ name: typeof item.name === "string" ? item.name : "", value: Number(item.value) }))
    .filter((item) => item.name && Number.isFinite(item.value));
  const sorted = [...entries].sort((a, b) => b.value - a.value);

  return { top: sorted.slice(0, 3), bottom: sorted.slice(-3).reverse() };
}

function MapRankLists({ option }: { option: Record<string, unknown> }) {
  const { top, bottom } = useMemo(() => mapRankLists(option), [option]);

  return (
    <div className="map-rank-lists" aria-label="都道府県ランキング">
      <div className="map-rank-list">
        <div className="map-rank-list__heading map-rank-list__heading--up">増加 TOP</div>
        {top.map((entry) => (
          <div className="map-rank-list__row" key={`up-${entry.name}`}>
            <span>{entry.name}</span>
            <span className="map-rank-list__value map-rank-list__value--up">{entry.value >= 0 ? "+" : ""}{entry.value.toFixed(2)}</span>
          </div>
        ))}
      </div>
      <div className="map-rank-list">
        <div className="map-rank-list__heading map-rank-list__heading--down">減少 TOP</div>
        {bottom.map((entry) => (
          <div className="map-rank-list__row" key={`down-${entry.name}`}>
            <span>{entry.name}</span>
            <span className="map-rank-list__value map-rank-list__value--down">{entry.value.toFixed(2)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CompositionBarLegend({
  segments,
  onSelect,
}: {
  segments: CompositionBarSegment[];
  onSelect: (segment: CompositionBarSegment) => void;
}) {
  return (
    <div className="composition-legend" aria-label="構成比の凡例">
      {segments.map((segment) => (
        <button
          type="button"
          key={segment.name}
          className="composition-legend__item"
          onClick={() => onSelect(segment)}
        >
          <span className="composition-legend__chip" style={{ background: segment.color }} aria-hidden="true" />
          <span className="composition-legend__name">{segment.name}</span>
          <span className="composition-legend__share">{segment.share.toFixed(1)}</span>
        </button>
      ))}
    </div>
  );
}

function CompositionBarNav({
  path,
  onBack,
  onReset,
}: {
  path: string[];
  onBack: () => void;
  onReset: () => void;
}) {
  const currentLabel = path[0];

  return (
    <div className="sunburst-drilldown" aria-label="構成バーの階層操作">
      {currentLabel ? (
        <span className="sunburst-drilldown__current" aria-label={`現状項目: ${currentLabel}`}>
          {currentLabel}
        </span>
      ) : null}
      <button type="button" onClick={onBack} disabled={!path.length}>
        上へ
      </button>
      <button type="button" onClick={onReset} disabled={!path.length}>
        全体
      </button>
    </div>
  );
}

function ChartPanel({
  chart,
  selectedValue,
  onSelectionChange,
}: {
  chart: DashboardChart;
  selectedValue?: string;
  onSelectionChange?: (key: string, value: string) => void;
}) {
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<ReturnType<typeof echarts.init> | null>(null);
  const activeVariant = selectedValue && chart.variants ? chart.variants[selectedValue] : undefined;
  const activeOption = activeVariant?.option ?? chart.option;
  const activeSummary = activeVariant?.summary ?? chart.summary;
  const activeTable = activeVariant?.table ?? chart.table;
  const activeTitle = activeVariant?.title ?? chart.title;
  const [compositionRootPath, setCompositionRootPath] = useState<string[]>([]);
  const [isTableOpen, setIsTableOpen] = useState(false);
  const targetSelection = chart.role === "target" ? chart.selection : undefined;
  const themedOption = useMemo(() => applyDesignTheme(activeOption, chart.type), [activeOption, chart.type]);
  const compositionView = useMemo(
    () => (chart.type === "composition-bar" ? buildCompositionBar(themedOption, compositionRootPath) : null),
    [themedOption, chart.type, compositionRootPath]
  );
  const displayOption = compositionView ? compositionView.option : themedOption;
  const headerNote = typeof themedOption.headerNote === "string" ? themedOption.headerNote : undefined;
  const footnote = typeof themedOption.footnote === "string" ? themedOption.footnote : undefined;
  const selectionControl =
    targetSelection && onSelectionChange ? (
      <SelectionControlGroup
        selections={[targetSelection]}
        values={{ [targetSelection.key]: selectedValue ?? targetSelection.defaultValue }}
        onSelectionChange={onSelectionChange}
      />
    ) : null;
  const syncCompositionSelection = useCallback(
    (nextPath: string[]) => {
      if (!chart.selection || !onSelectionChange) {
        return;
      }

      const nextValue = nextPath.length ? nextPath[nextPath.length - 1] : chart.selection.defaultValue;

      if (chart.selection.values.includes(nextValue)) {
        onSelectionChange(chart.selection.key, nextValue);
      }
    },
    [chart.selection, onSelectionChange]
  );
  const handleCompositionSegmentSelect = useCallback(
    (segment: CompositionBarSegment) => {
      syncCompositionSelection(segment.fullPath);

      if (segment.hasChildren) {
        setCompositionRootPath(segment.fullPath);
      }
    },
    [syncCompositionSelection]
  );
  const handleCompositionBack = useCallback(() => {
    setCompositionRootPath((currentPath) => {
      const nextPath = currentPath.slice(0, -1);
      syncCompositionSelection(nextPath);
      return nextPath;
    });
  }, [syncCompositionSelection]);
  const handleCompositionReset = useCallback(() => {
    setCompositionRootPath([]);
    syncCompositionSelection([]);
  }, [syncCompositionSelection]);

  useEffect(() => {
    setCompositionRootPath([]);
  }, [chart.id]);

  useEffect(() => {
    if (!isTableOpen) {
      return;
    }

    const previousOverflow = document.body.style.overflow;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsTableOpen(false);
      }
    };

    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isTableOpen]);

  useEffect(() => {
    if (!chartRef.current || chart.type === "composition-bar") {
      return;
    }

    const instance = echarts.init(chartRef.current, undefined, {
      renderer: "canvas"
    });
    chartInstanceRef.current = instance;

    if (chart.selection && chart.role === "source" && onSelectionChange) {
      instance.on("click", (params) => {
        const clickParams = params as ChartClickParams;
        const data = isRecord(clickParams.data) ? clickParams.data : undefined;
        const nextValue =
          typeof data?.selectionValue === "string"
            ? data.selectionValue
            : typeof clickParams.name === "string"
              ? clickParams.name
              : "";

        if (chart.selection?.values.includes(nextValue)) {
          onSelectionChange(chart.selection.key, nextValue);
        }
      });
    }

    const resize = () => {
      instance.resize();
    };
    const resizeObserver =
      typeof ResizeObserver === "undefined" || !chartRef.current ? null : new ResizeObserver(() => resize());
    resizeObserver?.observe(chartRef.current);
    window.addEventListener("resize", resize);
    window.requestAnimationFrame(resize);

    return () => {
      window.removeEventListener("resize", resize);
      resizeObserver?.disconnect();
      chartInstanceRef.current = null;
      instance.dispose();
    };
  }, [chart.id, chart.role, chart.selection, chart.type, onSelectionChange]);

  useEffect(() => {
    if (chart.type === "composition-bar") {
      return;
    }

    let active = true;

    const setOption = async () => {
      if (chart.type === "map") {
        await ensurePrefectureMap();
      }

      if (active) {
        chartInstanceRef.current?.setOption(displayOption, true);
      }
    };

    void setOption();

    return () => {
      active = false;
    };
  }, [chart.type, displayOption]);

  return (
    <section
      className={`chart-panel ${chart.variant === "showcase" ? "chart-panel--showcase" : "chart-panel--base"} ${
        chart.size === "tall" ? "chart-panel--tall" : ""
      } chart-panel--${chart.type}`}
      aria-labelledby={`${chart.id}-title`}
    >
      <div className="chart-panel__header">
        <div className="chart-title-block">
          <h3 id={`${chart.id}-title`}>{activeTitle}</h3>
          <InfoTooltip label={`${activeTitle}の説明`} text={chart.description} />
        </div>
        <div className="chart-panel__tools">
          {headerNote ? <span className="chart-panel__header-note">{headerNote}</span> : null}
          <button className="ghostbtn table-button" type="button" aria-haspopup="dialog" onClick={() => setIsTableOpen(true)}>
            表
          </button>
        </div>
      </div>
      {selectionControl}
      {chart.type === "composition-bar" ? (
        <CompositionBarNav path={compositionRootPath} onBack={handleCompositionBack} onReset={handleCompositionReset} />
      ) : null}
      {chart.type === "composition-bar" && compositionView ? (
        <>
          <div className="composition-bar-track" role="img" aria-label={activeSummary}>
            {compositionView.segments.map((segment) => (
              <button
                type="button"
                key={segment.name}
                className="composition-bar-track__segment"
                style={{ width: `${segment.share}%`, background: segment.color }}
                title={`${segment.name} ${segment.share.toFixed(1)}%`}
                onClick={() => handleCompositionSegmentSelect(segment)}
              />
            ))}
          </div>
          <CompositionBarLegend segments={compositionView.segments} onSelect={handleCompositionSegmentSelect} />
        </>
      ) : chart.type === "map" ? (
        <div className="map-with-ranks">
          <div ref={chartRef} className="chart-canvas chart-canvas--map" role="img" aria-label={activeSummary} />
          <MapRankLists option={displayOption} />
        </div>
      ) : (
        <div ref={chartRef} className="chart-canvas" role="img" aria-label={activeSummary} />
      )}
      {footnote ? <p className="chart-panel__footnote">{footnote}</p> : null}
      {isTableOpen ? (
        <TableModal
          chartId={chart.id}
          table={activeTable}
          title={`${activeTitle}の表`}
          onClose={() => setIsTableOpen(false)}
        />
      ) : null}
    </section>
  );
}

function TableModal({
  chartId,
  table,
  title,
  onClose,
}: {
  chartId: string;
  table: DataTable;
  title: string;
  onClose: () => void;
}) {
  return (
    <div className="table-modal" role="presentation">
      <button className="table-modal__scrim" type="button" aria-label="表を閉じる" onClick={onClose} />
      <section
        className="table-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${chartId}-table-title`}
      >
        <div className="table-modal__header">
          <h4 id={`${chartId}-table-title`}>{title}</h4>
          <button className="modal-close-button" type="button" onClick={onClose}>
            閉じる
          </button>
        </div>
        <div className="table-scroll table-scroll--modal">
          <DataTableView chartId={chartId} table={table} />
        </div>
      </section>
    </div>
  );
}

function DataTableView({ chartId, table }: { chartId: string; table: DataTable }) {
  return (
    <table>
      <thead>
        <tr>
          {table.columns.map((column) => (
            <th key={column} scope="col">
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {table.rows.map((row, rowIndex) => (
          <tr key={`${chartId}-${rowIndex}`}>
            {row.map((cell, cellIndex) => (
              <td key={`${chartId}-${rowIndex}-${cellIndex}`}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export {
  ChartPanel,
  mapRankLists,
  MapRankLists,
  CompositionBarLegend,
  CompositionBarNav,
  TableModal,
  DataTableView,
  type ChartClickParams,
};
